import type { Timestamp } from '@anytracker/core'

/**
 * Presentation of dates and times, which is platform work.
 *
 * `Intl` is a browser API, so this lives here rather than in core (ADR-0002).
 * Core owns what a timestamp *means* — the instant and the zone it was captured
 * in; this owns how it reads.
 *
 * Everything formats **in the zone the measurement was captured in**, not the
 * device's current one. An entry logged at 8pm in New York must still read 8pm
 * after the user flies home, or the app quietly rewrites their history every
 * time they travel (ADR-0006).
 */

const cache = new Map<string, Intl.DateTimeFormat>()

function formatter(timeZone: string, options: Intl.DateTimeFormatOptions) {
  const key = `${timeZone}|${JSON.stringify(options)}`
  let found = cache.get(key)
  if (!found) {
    // Constructing an Intl.DateTimeFormat is expensive relative to using one,
    // and a timeline renders hundreds of these.
    found = new Intl.DateTimeFormat(undefined, { ...options, timeZone })
    cache.set(key, found)
  }
  return found
}

function safeFormat(at: Timestamp, options: Intl.DateTimeFormatOptions): string {
  try {
    return formatter(at.timeZone, options).format(at.epochMs)
  } catch {
    // An unknown zone identifier would otherwise throw during render and blank
    // the screen. Falling back to the device zone shows a slightly wrong time;
    // throwing shows nothing at all.
    return new Intl.DateTimeFormat(undefined, options).format(at.epochMs)
  }
}

/** "12 Jul 2026" */
export function formatDate(at: Timestamp): string {
  return safeFormat(at, { day: 'numeric', month: 'short', year: 'numeric' })
}

/** "14:30" */
export function formatTime(at: Timestamp): string {
  return safeFormat(at, { hour: '2-digit', minute: '2-digit' })
}

/** "12 Jul 2026, 14:30" */
export function formatDateTime(at: Timestamp): string {
  return `${formatDate(at)}, ${formatTime(at)}`
}

/**
 * "12 Jul 2026" from a bare `YYYY-MM-DD` day label.
 *
 * A range boundary is a calendar day rather than a moment, so it carries no
 * zone to format in. Read as UTC deliberately: the string already names the
 * local day the user picked, and reinterpreting it in the device zone is what
 * makes a date picker show the day before the one that was chosen.
 */
export function formatDay(day: string): string {
  const parsed = Date.parse(`${day}T12:00:00Z`)
  if (Number.isNaN(parsed)) {
    return day
  }
  return formatter('UTC', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(parsed)
}

/**
 * "today", "yesterday", "3 days ago", or a date once that stops being useful.
 *
 * Relative wording earns its place only near the present. "1,247 days ago" is
 * strictly less informative than the date it replaces.
 */
export function formatRelativeDay(at: Timestamp, now: Timestamp): string {
  const days = Math.floor((now.epochMs - at.epochMs) / 86_400_000)
  if (days <= 0) {
    return 'today'
  }
  if (days === 1) {
    return 'yesterday'
  }
  if (days < 7) {
    return `${String(days)} days ago`
  }
  return formatDate(at)
}
